/**
 * Country Selector Component
 * Simplified version that avoids TypeScript issues while maintaining functionality
 */

import React, { useState, useRef, useEffect } from 'react';
import { ChevronDown, Search } from 'lucide-react';
import { 
  COUNTRY_DATA, 
  COMMON_COUNTRIES, 
  getCountryInfo,
  searchCountries,
  type CountryInfo
} from '../../constants/countries';

interface CountrySelectorProps {
  value: string;
  onChange: (countryCode: string) => void;
  className?: string;
  placeholder?: string;
  disabled?: boolean;
  error?: boolean;
  'data-testid'?: string;
}

const CountrySelector: React.FC<CountrySelectorProps> = ({
  value,
  onChange,
  className = '',
  placeholder = 'Select country...',
  disabled = false,
  error = false,
  'data-testid': testId = 'country-selector'
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  // Get filtered countries
  const getFilteredCountries = (): CountryInfo[] => {
    const allCountries = Object.values(COUNTRY_DATA).filter(country => country.isActive);

    if (searchQuery) {
      return searchCountries(searchQuery);
    }

    // Sort with common countries first
    return allCountries.sort((a, b) => {
      const aIsCommon = COMMON_COUNTRIES.includes(a.code as any);
      const bIsCommon = COMMON_COUNTRIES.includes(b.code as any);
      
      if (aIsCommon && !bIsCommon) return -1;
      if (!aIsCommon && bIsCommon) return 1;
      
      // Within common countries, maintain the order from COMMON_COUNTRIES
      if (aIsCommon && bIsCommon) {
        const aIndex = COMMON_COUNTRIES.indexOf(a.code as any);
        const bIndex = COMMON_COUNTRIES.indexOf(b.code as any);
        return aIndex - bIndex;
      }
      
      // For non-common countries, sort alphabetically
      return a.name.localeCompare(b.name);
    });
  };

  const filteredCountries = getFilteredCountries();
  const selectedCountry = getCountryInfo(value);

  // Handle keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!isOpen) return;

      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          setHighlightedIndex(prev => 
            prev < filteredCountries.length - 1 ? prev + 1 : 0
          );
          break;
        case 'ArrowUp':
          e.preventDefault();
          setHighlightedIndex(prev => 
            prev > 0 ? prev - 1 : filteredCountries.length - 1
          );
          break;
        case 'Enter':
          e.preventDefault();
          if (highlightedIndex >= 0 && highlightedIndex < filteredCountries.length) {
            onChange(filteredCountries[highlightedIndex].code);
            setIsOpen(false);
            setSearchQuery('');
          }
          break;
        case 'Escape':
          setIsOpen(false);
          setSearchQuery('');
          break;
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
      return () => document.removeEventListener('keydown', handleKeyDown);
    }
  }, [isOpen, highlightedIndex, filteredCountries, onChange]);

  // Reset highlighted index when search changes
  useEffect(() => {
    setHighlightedIndex(-1);
  }, [searchQuery]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setSearchQuery('');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Focus search input when dropdown opens
  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [isOpen]);

  const handleToggle = () => {
    if (disabled) return;
    setIsOpen(!isOpen);
    setSearchQuery('');
  };

  const handleCountrySelect = (country: CountryInfo) => {
    onChange(country.code);
    setIsOpen(false);
    setSearchQuery('');
  };

  const getDisplayText = () => {
    if (selectedCountry) {
      return `${selectedCountry.code} - ${selectedCountry.name}`;
    }
    return placeholder;
  };

  return (
    <div 
      className={`relative ${className}`} 
      ref={dropdownRef}
      data-testid={testId}
    >
      {/* Main button */}
      <button
        type="button"
        onClick={handleToggle}
        disabled={disabled}
        className={`
          w-full px-3 py-2 text-left bg-white border rounded-lg
          flex items-center justify-between
          transition-colors duration-200
          ${error 
            ? 'border-red-500 focus:ring-red-500' 
            : 'border-border-primary focus:ring-[var(--brand-primary)]'
          }
          ${disabled 
            ? 'opacity-50 cursor-not-allowed bg-bg-secondary' 
            : 'hover:border-border-secondary focus:outline-none focus:ring-2 focus:ring-opacity-50'
          }
          ${!selectedCountry ? 'text-text-tertiary' : 'text-[var(--text-primary)]'}
        `}
      >
        <span className="truncate">
          {getDisplayText()}
        </span>
        <ChevronDown 
          className={`w-5 h-5 text-text-muted transition-transform duration-200 ${
            isOpen ? 'transform rotate-180' : ''
          }`} 
        />
      </button>

      {/* Dropdown */}
      {isOpen && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-border-primary rounded-lg shadow-lg max-h-64 overflow-hidden">
          {/* Search input */}
          <div className="p-3 border-b border-border-primary">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-text-muted" />
              <input
                ref={searchInputRef}
                type="text"
                placeholder="Search countries..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-3 py-2 border border-border-primary rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[var(--brand-primary)] focus:ring-opacity-50"
              />
            </div>
          </div>

          {/* Countries list */}
          <div className="max-h-48 overflow-y-auto">
            {filteredCountries.length === 0 ? (
              <div className="px-3 py-2 text-sm text-text-tertiary">
                No countries found
              </div>
            ) : (
              filteredCountries.map((country, index) => (
                <button
                  key={country.code}
                  type="button"
                  onClick={() => handleCountrySelect(country)}
                  className={`
                    w-full px-3 py-2 text-left text-sm hover:bg-bg-secondary
                    flex items-center justify-between
                    ${index === highlightedIndex ? 'bg-[var(--brand-primary)]/10' : ''}
                    ${value === country.code ? 'bg-[var(--brand-primary)]/20 font-medium' : ''}
                  `}
                >
                  <div>
                    <span className="font-medium">{country.code}</span>
                    <span className="ml-2 text-text-secondary">{country.name}</span>
                    {country.localName && country.localName !== country.name && (
                      <span className="ml-1 text-xs text-text-tertiary">
                        ({country.localName})
                      </span>
                    )}
                  </div>
                  {COMMON_COUNTRIES.includes(country.code as any) && (
                    <span className="text-xs text-[var(--brand-primary)] font-medium">
                      Popular
                    </span>
                  )}
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default CountrySelector;